import React from "react";
import { Typography, Box, Grid } from "@mui/material";
import { useAppSelector } from "../../utils/hooks";
import {
  dateCustomFormatter,
  isCurrentAppPlan,
  isExtensionPremiumPlan,
} from "../../utils/utils";
import { getPlanDisplayInfo, PlanDisplayInfo } from "../settings/planUtils";

interface UsageStatsProps {
  // Add any props needed for customization
}

const UsageStats: React.FC<UsageStatsProps> = () => {
  // const plan = useAppSelector((state) => state.consumerSubscriptions.plan);

  // Fetch subscription data from Redux store
  const subscription = useAppSelector(
    (state) => state.consumerSubscriptions.subscription,
  );

  const subscriptions = subscription?.subscriptions;
  let renewalDate: any = "N/A";
  let planInfo: PlanDisplayInfo | null = null;

  if (subscriptions && subscriptions.length > 0) {
    const extensionSubs = subscriptions.filter((ss) => isCurrentAppPlan(ss));

    if (extensionSubs.length > 0) {
      const sub =
        extensionSubs.length > 1 && !isExtensionPremiumPlan(extensionSubs[0])
          ? extensionSubs[1]
          : extensionSubs[0];

      planInfo = getPlanDisplayInfo(sub);

      // Calculate renewal date (same approach as in SubscriptionDetails.tsx)
      let renewalDateValue;
      if (sub?.externalSubscriptionCurrentPeriodEndDate) {
        renewalDateValue = dateCustomFormatter(
          new Date(sub.externalSubscriptionCurrentPeriodEndDate),
        );
      } else if (sub?.initialStartDateTime) {
        const startDate = new Date(sub.initialStartDateTime);
        startDate.setDate(startDate.getDate() + (sub.days || 0)); // add # of days
        renewalDateValue = dateCustomFormatter(startDate);
      } else {
        renewalDateValue = "N/A";
      }

      renewalDate = renewalDateValue;
    }
  }

  // Calculate remaining requests
  const requestsRemaining = subscription?.checkAllowance
    ? subscription.checkAllowance - (subscription.checkUsage || 0)
    : 0;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: "0.2em",
          fontFamily: "Poppins",
        }}
      >
        <Typography
          style={{ fontWeight: "bold", color: "#161616" }}
          variant="h4"
          component="h1"
        >
          WriteAhead Usage
        </Typography>
        <Typography
          style={{ fontWeight: "bold", color: "#5a5a5a" }}
          variant="h6"
        >
          <span style={{ color: "#006CAC" }}>{requestsRemaining}</span> Requests
          Remaining
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 4,
          bgcolor: "#FFFFFF",
          borderRadius: 2,
          p: 3,
          fontFamily: "Poppins",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Grid container spacing={2} sx={{ flexGrow: 1, alignItems: "stretch" }}>
          <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                fontFamily: "Poppins",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 600,
                  fontSize: "30px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  verticalAlign: "middle",
                  color: "#413E3F",
                }}
              >
                Feedback Requests Made:
              </Typography>
              <Typography variant="h6" sx={{ fontFamily: "Poppins" }}>
                {subscription?.checkUsage || 0} /{" "}
                {subscription?.checkAllowance || 0}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                fontFamily: "Poppins",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              {planInfo?.label.toLowerCase().includes("introductory") ? (
                <>
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 600,
                      fontSize: "30px",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      verticalAlign: "middle",
                      color: "#413E3F",
                    }}
                  >
                    50 AI-powered feedback requests per month
                  </Typography>
                  <Typography variant="h6" sx={{ fontFamily: "Poppins" }}>
                    (automatically reset on the 1st of each month)
                  </Typography>
                </>
              ) : planInfo?.label.toLowerCase().includes("institutional") ? (
                <>
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 600,
                      fontSize: "30px",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      verticalAlign: "middle",
                      color: "#413E3F",
                    }}
                  >
                    Plan Expires:
                  </Typography>
                  <Typography variant="h6" sx={{ fontFamily: "Poppins" }}>
                    {renewalDate}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 600,
                      fontSize: "30px",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      verticalAlign: "middle",
                      color: "#413E3F",
                    }}
                  >
                    Plan Renews:
                  </Typography>
                  <Typography variant="h6" sx={{ fontFamily: "Poppins" }}>
                    {renewalDate}
                  </Typography>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default UsageStats;
